import { KeyboardEvent, useCallback, useRef } from 'react';
import { SearchX } from 'lucide-react';
import type { ClientProjectStatus } from '../../../../types/client-workspace';
import { STATUS_TAB_CONFIGS } from '../../../../types/client-workspace';
import { FOCUS_VISIBLE_CLASSES } from '../utils/accessibility';

export interface StatusTabNavigationProps {
  /** Currently active tab ID (defaults to 'all' on load) */
  activeTab: string;
  /** Count per status + 'all' key. Values can be numbers or "99+" strings. */
  statusCounts: Record<ClientProjectStatus | 'all', number | string>;
  /** Called when a tab is activated (click, Enter, Space, or Arrow key navigation) */
  onTabChange: (tabId: string) => void;
  /** Number of projects currently visible after filtering (used for empty state) */
  filteredCount?: number;
}

/**
 * Horizontal tab navigation for filtering projects by workflow status.
 *
 * Renders tabs from STATUS_TAB_CONFIGS with count badges and full keyboard
 * navigation (Arrow keys with wrap, Home/End, Enter/Space activation).
 *
 * Shows an empty state message when no projects match the selected tab.
 *
 * @see Requirements 2.1, 2.2, 2.3, 2.5, 2.6, 10.2
 */
export function StatusTabNavigation({
  activeTab,
  statusCounts,
  onTabChange,
  filteredCount,
}: StatusTabNavigationProps) {
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const getTabCount = (tabId: string, statusFilter: ClientProjectStatus[] | null): number | string => {
    if (statusFilter === null) {
      const allCount = statusCounts['all'] ?? 0;
      if (typeof allCount === 'string') return allCount; // already "99+"
      return allCount > 99 ? '99+' : allCount;
    }

    // Sum counts for all statuses in this tab's filter
    let total = 0;
    for (const status of statusFilter) {
      const count = statusCounts[status];
      if (typeof count === 'number') {
        total += count;
      } else if (count === '99+') {
        // If any individual status is 99+, the tab total is also 99+
        return '99+';
      }
    }

    return total > 99 ? '99+' : total;
  };

  const focusTabAtIndex = useCallback(
    (index: number) => {
      const wrappedIndex = ((index % STATUS_TAB_CONFIGS.length) + STATUS_TAB_CONFIGS.length) % STATUS_TAB_CONFIGS.length;
      tabRefs.current[wrappedIndex]?.focus();
    },
    [],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
      const tabCount = STATUS_TAB_CONFIGS.length;
      if (tabCount === 0) return;

      let nextIndex = index;

      switch (event.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          nextIndex = (index - 1 + tabCount) % tabCount;
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          nextIndex = (index + 1) % tabCount;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = tabCount - 1;
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          onTabChange(STATUS_TAB_CONFIGS[index].id);
          return;
        default:
          return;
      }

      event.preventDefault();
      focusTabAtIndex(nextIndex);
      onTabChange(STATUS_TAB_CONFIGS[nextIndex].id);
    },
    [focusTabAtIndex, onTabChange],
  );

  // Determine the active tab's label for the empty state message
  const activeTabConfig = STATUS_TAB_CONFIGS.find((t) => t.id === activeTab);
  const activeTabLabel = activeTabConfig?.label ?? 'selected';

  // Determine if we should show the empty state
  const showEmptyState = filteredCount !== undefined && filteredCount === 0 && activeTab !== 'all';

  return (
    <div className="flex flex-col gap-4">
      <div
        role="tablist"
        aria-label="Filter projects by status"
        aria-orientation="horizontal"
        className="flex gap-1 overflow-x-auto border-b border-white/10 px-2"
      >
        {STATUS_TAB_CONFIGS.map((tab, index) => {
          const isActive = activeTab === tab.id;
          const count = getTabCount(tab.id, tab.statusFilter);

          return (
            <button
              key={tab.id}
              role="tab"
              id={`tab-${tab.id}`}
              aria-selected={isActive}
              aria-controls={`tabpanel-${tab.id}`}
              tabIndex={isActive ? 0 : -1}
              type="button"
              className={[
                'flex shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-medium transition-colors min-h-11',
                FOCUS_VISIBLE_CLASSES,
                isActive
                  ? 'border-sky-500 text-white'
                  : 'border-transparent text-slate-400 hover:text-slate-200',
              ].join(' ')}
              onClick={() => onTabChange(tab.id)}
              onKeyDown={(event) => handleKeyDown(event, index)}
              ref={(node) => {
                tabRefs.current[index] = node;
              }}
            >
              {tab.label}
              <span
                className={[
                  'inline-flex min-w-5 items-center justify-center rounded-full px-1.5 py-0.5 text-xs font-semibold',
                  isActive
                    ? 'bg-sky-500/20 text-sky-300'
                    : 'bg-white/5 text-slate-500',
                ].join(' ')}
                aria-label={`${count} projects`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Empty state when no projects match the selected tab */}
      {showEmptyState && (
        <div
          role="tabpanel"
          id={`tabpanel-${activeTab}`}
          aria-labelledby={`tab-${activeTab}`}
          className="flex min-h-48 flex-col items-center justify-center gap-3 rounded-xl border border-white/10 bg-slate-900/50 p-6 text-center"
          data-testid="status-tab-empty-state"
        >
          <SearchX className="text-slate-500" aria-hidden="true" />
          <p className="text-sm text-slate-400">
            No projects currently have the &ldquo;{activeTabLabel}&rdquo; status.
          </p>
        </div>
      )}
    </div>
  );
}
